import { useQuery, useQueryClient } from "@tanstack/react-query";
import { validateConfiguration } from "../services/validationService";
import type {
	ProductDto,
	CompatibilityValidationResult,
	ValidateConfigurationResponseDto,
} from "../types/validation";
import { getProductsCacheKey } from "../utils/productUtils";

export const COMPATIBILITY_VALIDATION_QUERY_KEY = "compatibilityValidation";

interface UseCompatibilityValidationParams {
	configBundleId: string;
	selectedProducts: ProductDto[];
	enabled?: boolean;
}

/**
 * Hook to validate the current configuration
 * Uses TanStack Query to manage validation state
 *
 * Creates a unique cache entry for each configuration based on product IDs.
 * When the configuration changes, a new validation is triggered.
 * Previously validated configurations are cached for quick access.
 */
export function useCompatibilityValidation({
	configBundleId,
	selectedProducts,
	enabled = true,
}: UseCompatibilityValidationParams) {

	const productIds = getProductsCacheKey(selectedProducts);

	return useQuery<ValidateConfigurationResponseDto, Error, CompatibilityValidationResult>({
		queryKey: [COMPATIBILITY_VALIDATION_QUERY_KEY, configBundleId, productIds],
		queryFn: () => validateConfiguration(selectedProducts),
		enabled: enabled && selectedProducts.length > 0,
		staleTime: 30 * 1000,
		gcTime: 5 * 60 * 1000,
		select: (response): CompatibilityValidationResult => {
			const errorCount = response.errors.length;
			const warningCount = response.warnings.length;
			const successCount = response.passedRules.length;
			const totalRules = errorCount + warningCount + successCount;

			return {
				isValid: response.isValid,
				errors: response.errors,
				warnings: response.warnings,
				passedRules: response.passedRules,
				errorCount,
				warningCount,
				successCount,
				totalRules,
			};
		},
	});
}

export function getValidationResultsFromCache(
	queryClient: ReturnType<typeof useQueryClient>,
	configBundleId: string,
	selectedProducts: ProductDto[]
): CompatibilityValidationResult | undefined {
	const productIds = getProductsCacheKey(selectedProducts);

	return queryClient.getQueryData<CompatibilityValidationResult>([
		COMPATIBILITY_VALIDATION_QUERY_KEY,
		configBundleId,
		productIds,
	]);
}